import { useQuery } from "@tanstack/react-query";
import { Component, useState, useEffect, useMemo, type ErrorInfo, type ReactNode } from "react";
import { CircleMarker, LayersControl, MapContainer, Popup, TileLayer } from "react-leaflet";
import { LatLngBounds } from "leaflet";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";

import { IntelApi } from "../ops/intel/api";
import { POIApi } from "../ops/poi/api";
import { TasksApi } from "../ops/tasks/api";
import { PerformanceApi } from "../ops/performance/api";
import { ReportsApi } from "../ops/reports/api";
import { HierarchyApi, type Branch, type Constituency, type Ward } from "../admin/hierarchy/api";

type Level = "constituency" | "ward" | "branch";
type Source = "intel" | "poi" | "tasks" | "performance" | "threats";

const ZAMBIA_CENTER: [number, number] = [-13.13, 27.85];
const ZAMBIA_BOUNDS = new LatLngBounds([-18.5, 21.0], [-7.5, 34.5]);

const SOURCE_LABELS: Record<Source, string> = {
  intel: "Intel",
  poi: "POI",
  tasks: "Tasks",
  performance: "Performance",
  threats: "Threats",
};

const SOURCE_DETAIL_URL: Record<Source, (id: string) => string> = {
  intel: () => `/app/intel`,
  poi: (id) => `/app/poi/${id}`,
  tasks: (id) => `/app/tasks/${id}`,
  performance: () => `/app/performance`,
  threats: () => `/app/threats`,
};

const SUB_FILTERS: Record<Source, { value: string; label: string; field: string }[]> = {
  threats: [
    { value: "party", label: "Party", field: "threatTo" },
    { value: "president", label: "President", field: "threatTo" },
    { value: "mp", label: "MP", field: "threatTo" },
    { value: "party_member", label: "Party member", field: "threatTo" },
    { value: "branch", label: "Branch", field: "threatTo" },
    { value: "ward", label: "Ward", field: "threatTo" },
    { value: "constituency", label: "Constituency", field: "threatTo" },
    { value: "other", label: "Other", field: "threatTo" },
  ],
  intel: [
    { value: "critical", label: "Critical", field: "severity" },
    { value: "high", label: "High", field: "severity" },
    { value: "medium", label: "Medium", field: "severity" },
    { value: "low", label: "Low", field: "severity" },
  ],
  poi: [
    { value: "high", label: "High threat", field: "threatLevel" },
    { value: "medium", label: "Medium threat", field: "threatLevel" },
    { value: "low", label: "Low threat", field: "threatLevel" },
  ],
  tasks: [
    { value: "urgent", label: "Urgent", field: "priority" },
    { value: "high", label: "High", field: "priority" },
    { value: "medium", label: "Medium", field: "priority" },
    { value: "low", label: "Low", field: "priority" },
  ],
  performance: [
    { value: "excellent", label: "Excellent", field: "rating" },
    { value: "good", label: "Good", field: "rating" },
    { value: "satisfactory", label: "Satisfactory", field: "rating" },
    { value: "poor", label: "Poor", field: "rating" },
    { value: "failing", label: "Failing", field: "rating" },
  ],
};

const BUCKET_COLORS: Record<string, string> = {
  critical: "#b91c1c", high: "#dc2626", urgent: "#dc2626",
  medium: "#d97706", low: "#059669",
  secret: "#7c3aed", restricted: "#dc2626", unclassified: "#059669",
  excellent: "#059669", good: "#16a34a", satisfactory: "#d97706", poor: "#ea580c", failing: "#dc2626",
  very_high: "#16a34a", very_low: "#dc2626",
  unknown: "#64748b",
};

const SOURCE_COLOR: Record<Source, string> = {
  intel: "#dc2626",
  poi: "#7c3aed",
  tasks: "#059669",
  performance: "#d97706",
  threats: "#b91c1c",
};

interface MatchedItem {
  id: string;
  title: string;
  bucket: string;
}

interface MapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  items: MatchedItem[];
  breakdown: Record<string, number>;
}

export function MapScreen() {
  const [level, setLevel] = useState<Level>("constituency");
  const [activeSource, setActiveSource] = useState<Source>("intel");
  const [subFilter, setSubFilter] = useState<string>("all");

  // Reset sub-filter when source changes.
  useEffect(() => { setSubFilter("all"); }, [activeSource]);

  const constituencies = useQuery({ queryKey: ["constituencies"], queryFn: HierarchyApi.listConstituencies });
  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const intel = useQuery({ queryKey: ["intel"], queryFn: () => IntelApi.list() });
  const poi = useQuery({ queryKey: ["poi"], queryFn: () => POIApi.list() });
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => TasksApi.list() });
  const perf = useQuery({ queryKey: ["performance"], queryFn: () => PerformanceApi.list() });
  const threats = useQuery({ queryKey: ["report.threats"], queryFn: () => ReportsApi.threats() });

  const areas = useMemo(() => {
    if (level === "constituency") return (constituencies.data?.items ?? []) as { id: string; name: string; lat?: number | null; lng?: number | null }[];
    if (level === "ward") return (scope.data?.wards ?? []) as Ward[];
    return (scope.data?.branches ?? []) as Branch[];
  }, [level, constituencies.data, scope.data]);

  const points: MapPoint[] = useMemo(() => {
    const map = new Map<string, MapPoint>();
    for (const a of areas) {
      const lat = (a as Constituency | Ward | Branch).lat;
      const lng = (a as Constituency | Ward | Branch).lng;
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      map.set(a.id, { id: a.id, name: a.name, lat, lng, items: [], breakdown: {} });
    }

    function locate(it: { branchId?: string | null; wardId?: string | null; constituencyId?: string | null; scopeId?: string; scopeLevel?: string }): string | null | undefined {
      if (level === "constituency") return it.constituencyId ?? (it.scopeLevel === "constituency" ? it.scopeId : null);
      if (level === "ward") return it.wardId ?? (it.scopeLevel === "ward" ? it.scopeId : null);
      return it.branchId ?? (it.scopeLevel === "branch" ? it.scopeId : null);
    }

    const sourceData: Record<Source, { items: any[]; bucketField: string; titleField: string }> = {
      intel: { items: intel.data?.items ?? [], bucketField: "severity", titleField: "title" },
      poi: { items: poi.data?.items ?? [], bucketField: "threatLevel", titleField: "fullName" },
      tasks: { items: tasks.data?.items ?? [], bucketField: "priority", titleField: "title" },
      performance: { items: perf.data?.items ?? [], bucketField: "rating", titleField: "subjectUid" },
      threats: { items: threats.data?.items ?? [], bucketField: "threatTo", titleField: "title" },
    };

    const { items, bucketField, titleField } = sourceData[activeSource];
    for (const it of items) {
      const id = locate(it);
      if (!id) continue;
      const p = map.get(id);
      if (!p) continue;
      const bucket = (it as Record<string, string | undefined>)[bucketField] ?? "unknown";
      if (subFilter !== "all" && bucket !== subFilter) continue;
      const titleRaw = (it as Record<string, string | undefined>)[titleField];
      const title = titleRaw && String(titleRaw).trim()
        ? String(titleRaw)
        : `(${SOURCE_LABELS[activeSource]} ${(it as { id: string }).id.slice(0, 6)})`;
      p.items.push({ id: (it as { id: string }).id, title, bucket });
      p.breakdown[bucket] = (p.breakdown[bucket] ?? 0) + 1;
    }

    return [...map.values()];
  }, [areas, level, activeSource, subFilter, intel.data, poi.data, tasks.data, perf.data, threats.data]);

  const noAreas = areas.length === 0;
  const noCoordinates = areas.length > 0 && points.length === 0;
  const isLoading = constituencies.isLoading || scope.isLoading;

  return (
    <div>
      <header style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Map</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Where the activity is. Pick a data source, then optionally filter by severity / threat / priority.
          Bubble size shows the count; click a bubble for the records behind it.
        </p>
      </header>

      {/* PRIMARY: which data source */}
      <div role="tablist" style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 12, flexWrap: "wrap" }}>
        {(Object.keys(SOURCE_LABELS) as Source[]).map((s) => (
          <button
            key={s}
            role="tab"
            aria-selected={activeSource === s}
            onClick={() => setActiveSource(s)}
            className="ghost"
            style={{
              background: "transparent",
              color: activeSource === s ? SOURCE_COLOR[s] : "var(--muted)",
              border: "none",
              borderBottom: `2px solid ${activeSource === s ? SOURCE_COLOR[s] : "transparent"}`,
              borderRadius: 0,
              padding: "10px 14px",
              marginBottom: -1,
              fontWeight: 600,
            }}
          >
            {SOURCE_LABELS[s]}
          </button>
        ))}
      </div>

      {/* SECONDARY: severity / priority / threat sub-filters for the chosen source */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <FilterChip
          active={subFilter === "all"}
          color={SOURCE_COLOR[activeSource]}
          onClick={() => setSubFilter("all")}
        >All</FilterChip>
        {SUB_FILTERS[activeSource].map((opt) => (
          <FilterChip
            key={opt.value}
            active={subFilter === opt.value}
            color={BUCKET_COLORS[opt.value] ?? SOURCE_COLOR[activeSource]}
            onClick={() => setSubFilter(opt.value)}
          >{opt.label}</FilterChip>
        ))}
      </div>

      {/* TERTIARY: hierarchy level — by constituency / ward / branch */}
      <div role="tablist" style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {(["constituency", "ward", "branch"] as Level[]).map((l) => (
          <button
            key={l}
            role="tab"
            aria-selected={level === l}
            className="ghost"
            onClick={() => setLevel(l)}
            style={{
              background: level === l ? "var(--accent-soft)" : "transparent",
              color: level === l ? "var(--accent)" : "var(--muted)",
              border: `1px solid ${level === l ? "var(--accent)" : "var(--border)"}`,
              borderRadius: 999,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          >
            By {l}
          </button>
        ))}
      </div>

      {isLoading && <div className="muted" style={{ padding: 12 }}>Loading hierarchy…</div>}
      {!isLoading && noAreas && (
        <div className="info" style={{ marginBottom: 12 }}>
          No {level} records visible. Build the hierarchy first under <strong>Setup → Hierarchy</strong>.
        </div>
      )}
      {noCoordinates && (
        <div className="info" style={{ marginBottom: 12 }}>
          {areas.length} {level}{areas.length === 1 ? "" : "s"} found, but none have coordinates yet.
          Add lat / lng when creating or editing each entry under <strong>Setup → Hierarchy</strong>,
          then come back here. Or hit <strong>Reset and re-seed</strong> on the Home page to get the
          seeded Zambia coordinates.
        </div>
      )}

      <MapErrorBoundary>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <MapContainer
            center={ZAMBIA_CENTER}
            zoom={6}
            minZoom={5}
            maxZoom={16}
            maxBounds={ZAMBIA_BOUNDS}
            maxBoundsViscosity={1.0}
            style={{ height: 560, width: "100%" }}
            scrollWheelZoom
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Streets">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Terrain">
                <TileLayer
                  attribution='Map data: &copy; OpenStreetMap, SRTM | Style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer
                  attribution='Tiles &copy; Esri &mdash; Sources: Esri, DigitalGlobe, GeoEye, Earthstar Geographics, USDA, USGS, AeroGRID'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            {points.filter((p) => p.items.length > 0).map((p) => (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lng]}
                radius={radiusFor(p.items.length)}
                pathOptions={{
                  color: bubbleColor(p, activeSource, subFilter),
                  fillColor: bubbleColor(p, activeSource, subFilter),
                  fillOpacity: 0.45,
                  weight: 1.5,
                }}
              >
                <Popup maxWidth={320}>
                  <div style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{p.name}</div>
                    <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
                      {SOURCE_LABELS[activeSource]} · {p.items.length} record{p.items.length === 1 ? "" : "s"}
                      {subFilter !== "all" && (
                        <> · filter: <strong>{SUB_FILTERS[activeSource].find((o) => o.value === subFilter)?.label}</strong></>
                      )}
                    </div>

                    {/* Breakdown bars */}
                    {Object.entries(p.breakdown).length > 1 && (
                      <div style={{ marginBottom: 8 }}>
                        {Object.entries(p.breakdown)
                          .sort(([, a], [, b]) => b - a)
                          .map(([bucket, n]) => (
                            <div key={bucket} style={{ fontSize: 11, marginBottom: 2 }}>
                              <span style={{ color: BUCKET_COLORS[bucket] ?? "var(--muted)", fontWeight: 600 }}>{labelOf(activeSource, bucket)}</span>
                              : {n}
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Top records */}
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 6 }}>
                      <div className="muted" style={{ fontSize: 11, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Records
                      </div>
                      {p.items.slice(0, 6).map((it) => (
                        <div key={it.id} style={{ fontSize: 12, marginBottom: 4, lineHeight: 1.4 }}>
                          <span style={{
                            display: "inline-block", width: 6, height: 6, borderRadius: "50%",
                            background: BUCKET_COLORS[it.bucket] ?? "var(--muted)", marginRight: 6, verticalAlign: "middle",
                          }} />
                          <Link to={SOURCE_DETAIL_URL[activeSource](it.id)} style={{ color: "var(--text)", textDecoration: "none", borderBottom: "1px dotted var(--muted)" }}>
                            {it.title}
                          </Link>
                          <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                            {labelOf(activeSource, it.bucket)}
                          </span>
                        </div>
                      ))}
                      {p.items.length > 6 && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          + {p.items.length - 6} more not shown.
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}

            {points.filter((p) => p.items.length === 0).map((p) => (
              <CircleMarker
                key={`empty-${p.id}`}
                center={[p.lat, p.lng]}
                radius={4}
                pathOptions={{ color: "#cbd5e1", fillColor: "#cbd5e1", fillOpacity: 0.4, weight: 1 }}
              >
                <Popup>
                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      No {SOURCE_LABELS[activeSource].toLowerCase()} {subFilter !== "all" ? `(${subFilter})` : ""} here.
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </MapErrorBoundary>
    </div>
  );
}

function FilterChip({
  active, color, onClick, children,
}: {
  active: boolean;
  color: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 999,
        border: `1px solid ${active ? color : "var(--border)"}`,
        color: active ? color : "var(--muted)",
        background: active ? color + "1a" : "transparent",
        cursor: "pointer", fontSize: 13, fontWeight: 500,
      }}
    >
      {active && (
        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: color }} />
      )}
      {children}
    </button>
  );
}

function radiusFor(count: number): number {
  return Math.max(8, Math.min(40, Math.round(8 + Math.sqrt(count) * 6)));
}

// When a sub-filter is active, colour the bubble by it. Otherwise pick the
// dominant bucket among the items at this point.
function bubbleColor(p: MapPoint, source: Source, subFilter: string): string {
  if (subFilter !== "all") return BUCKET_COLORS[subFilter] ?? SOURCE_COLOR[source];
  const entries = Object.entries(p.breakdown);
  entries.sort(([, a], [, b]) => b - a);
  const dominant = entries[0]?.[0];
  return dominant ? (BUCKET_COLORS[dominant] ?? SOURCE_COLOR[source]) : SOURCE_COLOR[source];
}

function labelOf(source: Source, bucket: string): string {
  const opt = SUB_FILTERS[source].find((o) => o.value === bucket);
  return opt ? opt.label : bucket.replace(/_/g, " ");
}

class MapErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Map render failed:", error, info);
  }
  reset = () => this.setState({ error: null });
  render() {
    if (this.state.error) {
      return (
        <div className="card" style={{ padding: 24 }}>
          <div className="error" style={{ marginBottom: 8 }}>
            <strong>The map failed to render.</strong>
          </div>
          <p className="muted" style={{ fontSize: 13 }}>
            {this.state.error.message || "Unexpected error from the map layer."}
          </p>
          <button onClick={this.reset} style={{ marginTop: 8 }}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}
